const API_BASE = '/api';

export interface ApiBead {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly colorHex: string;
  readonly colorLab: { readonly L: number; readonly a: number; readonly b: number };
  readonly finish: string;
  readonly size: string;
  readonly isAvailable: boolean;
  readonly manufacturer: { readonly name: string; readonly slug: string };
  readonly deltaE?: number;
}

export interface ApiProject {
  readonly id: string;
  readonly name: string;
  readonly gridData: Record<string, unknown>;
  readonly thumbnail: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface ApiResponse<T> {
  readonly success: boolean;
  readonly data: T;
  readonly meta?: { readonly total: number; readonly limit: number; readonly offset: number };
  readonly error?: string;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

// ─── Beads ────────────────────────────────────────────

export async function fetchBeads(params: {
  search?: string;
  color?: string;
  finish?: string;
  limit?: number;
  offset?: number;
}): Promise<ApiResponse<ApiBead[]>> {
  const searchParams = new URLSearchParams();
  if (params.search) searchParams.set('search', params.search);
  if (params.color) searchParams.set('color', params.color);
  if (params.finish) searchParams.set('finish', params.finish);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.offset) searchParams.set('offset', String(params.offset));

  const res = await fetch(`${API_BASE}/beads?${searchParams.toString()}`);
  return res.json();
}

export async function fetchBead(id: string): Promise<ApiResponse<ApiBead>> {
  const res = await fetch(`${API_BASE}/beads/${id}`);
  return res.json();
}

// ─── Projects ─────────────────────────────────────────

export async function fetchProjects(): Promise<ApiResponse<ApiProject[]>> {
  const res = await fetch(`${API_BASE}/projects`, { headers: authHeaders() });
  return res.json();
}

export async function fetchProject(id: string): Promise<ApiResponse<ApiProject>> {
  const res = await fetch(`${API_BASE}/projects/${id}`, { headers: authHeaders() });
  return res.json();
}

export async function createProject(data: {
  name: string;
  gridData: Record<string, unknown>;
}): Promise<ApiResponse<ApiProject>> {
  const res = await fetch(`${API_BASE}/projects`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function updateProject(
  id: string,
  data: { name?: string; gridData?: Record<string, unknown>; thumbnail?: string },
): Promise<ApiResponse<ApiProject>> {
  const res = await fetch(`${API_BASE}/projects/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deleteProject(id: string): Promise<ApiResponse<null>> {
  const res = await fetch(`${API_BASE}/projects/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return res.json();
}

// ─── Patterns ─────────────────────────────────────────

export interface ApiPattern {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly technique: string;
  readonly category: string;
  readonly difficulty: string;
  readonly tags: readonly string[];
  readonly width: number;
  readonly height: number;
  readonly thumbnail: string | null;
}

export interface ApiPatternFull extends ApiPattern {
  readonly gridData: {
    readonly palette: readonly { readonly hex: string; readonly label: string }[];
    readonly grid: readonly (readonly number[])[];
  };
}

export async function fetchPatterns(params?: {
  category?: string;
  technique?: string;
  search?: string;
  limit?: number;
}): Promise<ApiResponse<ApiPattern[]>> {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.technique) searchParams.set('technique', params.technique);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const res = await fetch(`${API_BASE}/patterns?${searchParams.toString()}`);
  return res.json();
}

export async function fetchPattern(slug: string): Promise<ApiResponse<ApiPatternFull>> {
  const res = await fetch(`${API_BASE}/patterns/${slug}`);
  return res.json();
}
